import type { StorefrontEventName } from '@xshop/shared';
import { injectScript, type AppEventDetail, type BrowserApp } from './browserApp';

// Microsoft Clarity: heatmaps and session recordings. The standard loader queues calls until the
// tag arrives; checkout sessions are kept and a purchase is marked as a Clarity event.
// https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

// Returns the page's clarity() queue, creating it the way Clarity's own snippet does.
const clarity = (): ClarityFn => {
  const target = window as Window & { clarity?: ClarityFn };
  target.clarity ??= Object.assign((...args: unknown[]) => { (target.clarity!.q ??= []).push(args); }, { q: [] as unknown[][] });
  return target.clarity;
};

// Loads Clarity's tag for the project, once.
const install = (settings: Record<string, string | boolean>, consented: boolean): void => {
  const projectId = String(settings.projectId || '');
  if (!/^[a-z0-9]{6,20}$/.test(projectId) || document.getElementById('xshop-clarity')) return;
  clarity();
  if (consented) clarity()('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' });
  injectScript('xshop-clarity', `https://www.clarity.ms/tag/${projectId}`);
};

// Tags checkout and purchase steps so the merchant can find the sessions that mattered.
const track = (name: StorefrontEventName, detail: AppEventDetail): void => {
  if (name === 'begin_checkout') clarity()('upgrade', 'checkout');
  if (name === 'purchase') {
    clarity()('event', 'purchase');
    if (detail.orderNumber) clarity()('set', 'order', detail.orderNumber);
  }
};

export const clarityApp: BrowserApp = { install, track };
